import { useState } from "react";

const TEAL = "#0d9488";

const btnStyle: React.CSSProperties = {
  border: "none", borderRadius: 7, padding: "9px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer",
};

/**
 * The shared "install the plugin" content. Rendered both inside SchemaSetupDialog
 * and in the Settings → WP Integration tab, so they stay identical.
 */
export function WpHelperContent({ zipUrl, updaterZipUrl = "/LSG-updater.zip", whiteLabel = false, brandName = "" }: { zipUrl: string; updaterZipUrl?: string; whiteLabel?: boolean; brandName?: string }) {
  const [brand, setBrand] = useState(brandName);
  const hasBrand = brand.trim().length > 0;

  return (
    <>
      <p className="st-p">
        The <strong>Local SEO Garage (LSG)</strong> plugin connects your site to the platform so it can <strong>make changes to
        your existing content</strong> or <strong>creation of new pages</strong> programmatically. It allows <strong>implementation of JSON-LD schema</strong> into each page&apos;s <code>&lt;head&gt;</code> (works on
        any page builder) and <strong>adds a schema meta box</strong> in the page editor to view or
        edit it. It enables <strong>internal link insertions</strong> including into Elementor pages.
        The plugin also <strong>enables tags on Pages</strong> (not just Posts). You only install it once per site.
        Enjoy efficiency and the unfair advantage.
      </p>

      {whiteLabel && (
        <div className="st-card">
          <h3 className="st-wpb-h">White-label (Agency)</h3>
          <p className="st-p">
            Give the plugin your own name so your client never sees &ldquo;Local SEO Garage.&rdquo; Enter the name below, then
            download &mdash; the plugin installs <strong>already showing that name</strong> (and keeps it through auto-updates).
            The <strong>folder and the PHP file are also named after your agency</strong> (e.g. <code>Acme Agency/Acme Agency.php</code>),
            so it stays branded even in the site&apos;s files.
          </p>
          <input
            className="st-input" type="text" value={brand} maxLength={60}
            onChange={e => setBrand(e.target.value)}
            placeholder="e.g. Acme SEO Suite"
            style={{ marginBottom: 10 }}
          />
          <a
            href={hasBrand ? `/api/plugin/download-branded?brand=${encodeURIComponent(brand.trim())}` : undefined}
            download
            onClick={e => { if (!hasBrand) e.preventDefault(); }}
            className={`st-btn purple${hasBrand ? "" : " disabled"}`}
            style={{ textDecoration: "none", display: "inline-block" }}
          >
            ⬇ Download branded plugin (.zip)
          </a>
        </div>
      )}

      <div className="st-card">
        <h3 className="st-wpb-h">Install the plugin</h3>
        <p className="st-p">
          It <strong>survives theme switches and updates</strong>, and is required for editing Elementor pages, proper
          connection, and functionality. Download the plugin, then in WP Admin go to
          <strong> Plugins → Add New → Upload Plugin</strong>, choose the file, and <strong>Activate</strong>.
        </p>
        <a href={zipUrl} download className="st-btn orange" style={{ textDecoration: "none", display: "inline-block" }}>
          ⬇ Download plugin (.zip)
        </a>
      </div>

      <div className="st-card">
        <h3 className="st-wpb-h">Multisite networks only — LSG Updater (optional)</h3>
        <p className="st-p">
          On a WordPress <strong>Multisite</strong> network, also install this small companion and <strong>Network Activate</strong> it once.
          It lets the main plugin update with one click from <strong>Network Admin → Plugins</strong> (the main plugin stays
          per-site activated and inactive by default). <strong>Skip this on single-site installs</strong> — the main plugin updates itself there.
        </p>
        <a href={updaterZipUrl} download className="st-btn orange" style={{ textDecoration: "none", display: "inline-block" }}>
          ⬇ Download LSG Updater (.zip)
        </a>
      </div>
    </>
  );
}

interface Props {
  open: boolean;
  zipUrl: string;
  /** Probes the live site for the helper. Resolves true once it's active. */
  onVerify: () => Promise<boolean>;
  /** Helper confirmed installed — proceed with the pending approval. */
  onVerified: () => void;
  /** Dismiss without installing. */
  onClose: () => void;
}

export function SchemaSetupDialog({ open, zipUrl, onVerify, onVerified, onClose }: Props) {
  const [status, setStatus] = useState<"idle" | "checking" | "failed">("idle");

  if (!open) return null;

  async function handleVerify() {
    setStatus("checking");
    const ok = await onVerify().catch(() => false);
    if (ok) { setStatus("idle"); onVerified(); }
    else setStatus("failed");
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 4000, background: "rgba(15,23,42,.55)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "5vh 16px",
      }}
      onClick={onClose}
    >
      <div
        className="st-light"
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, maxWidth: 660, width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,.35)", padding: "24px 26px", color: "#334155", lineHeight: 1.5,
        }}
      >
        <h2 style={{ margin: "0 0 6px", fontSize: 19, color: "#0f172a", fontFamily: "'Oswald', 'Barlow Condensed', 'Inter', sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" }}>Enable one-click WordPress changes</h2>

        <WpHelperContent zipUrl={zipUrl} />

        {/* Verify + actions */}
        <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            style={{ ...btnStyle, borderRadius: 0, background: TEAL, color: "#fff", opacity: status === "checking" ? 0.7 : 1 }}
            onClick={handleVerify}
            disabled={status === "checking"}
          >
            {status === "checking" ? "Checking…" : "I've added it — Verify & continue"}
          </button>
          <button style={{ ...btnStyle, borderRadius: 0, background: "#e2e8f0", color: "#334155" }} onClick={onClose}>
            Cancel
          </button>
          {status === "failed" && (
            <span style={{ fontSize: 12.5, color: "#dc2626" }}>
              Not detected yet — make sure you saved the snippet (or activated the plugin), then try again.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
